import { useMemo } from 'react'

import { useAuth } from '@/auth/use-auth'
import { PERMISSIONS, type PermissionCode } from '@/lib/permissions'

/**
 * What this user may do in the ledger.
 *
 * Every code below comes from `src/lib/permissions.ts`, which the server emits from its own
 * catalogue. Nothing is written as a hand-typed string: a ledger permission that does not exist in
 * that file is a bug, and inventing one here would hide the difference between the two sides rather
 * than expose it.
 *
 * Presentation only. The server re-checks each command against the live catalogue, so a hidden
 * control is not a closed door and a visible one is not an open door. That matters more in this
 * module than in most, because several ledger screens are directly addressable — hiding the
 * navigation item to `/ledger/journals/new` gates nothing, which is why the screen itself carries
 * the gate.
 *
 * The `.approve` codes are held here as well as the acting ones. They authorise nothing on these
 * screens; they are what lets a countersigned action tell the maker, *before* the click, whether
 * confirming it will complete the work or merely queue it.
 *
 * Two of the codes are not the ledger's own and are held anyway, because two ledger screens depend
 * on them:
 *
 * - `ops.audit.view` gates the audit trail, which is the only record of *who* posted an entry and
 *   whether anyone countersigned it. A `ledger.journal.view` holder without it can read a journal
 *   and its lines and cannot read who raised it, and the screen says exactly that.
 * - `loans.view` gates the classification scheme list, which is the rates table behind a provision
 *   run. A ledger-only role may run the provision and be unable to read the scheme it applied.
 */
export const LEDGER_PERMISSIONS = {
  accountManage: PERMISSIONS.ledger.accountManage,
  accountManageApprove: PERMISSIONS.ledger.accountManageApprove,
  accountView: PERMISSIONS.ledger.accountView,
  dayClose: PERMISSIONS.ledger.dayClose,
  dayOpen: PERMISSIONS.ledger.dayOpen,
  dayReopen: PERMISSIONS.ledger.dayReopen,
  dayReopenApprove: PERMISSIONS.ledger.dayReopenApprove,
  driftResolve: PERMISSIONS.ledger.driftResolve,
  driftView: PERMISSIONS.ledger.driftView,
  journalManual: PERMISSIONS.ledger.journalManual,
  journalManualApprove: PERMISSIONS.ledger.journalManualApprove,
  journalReverse: PERMISSIONS.ledger.journalReverse,
  journalReverseApprove: PERMISSIONS.ledger.journalReverseApprove,
  journalView: PERMISSIONS.ledger.journalView,
  periodClose: PERMISSIONS.ledger.periodClose,
  periodReopen: PERMISSIONS.ledger.periodReopen,
  periodReopenApprove: PERMISSIONS.ledger.periodReopenApprove,
  provisionRun: PERMISSIONS.ledger.provisionRun,
  trialBalanceView: PERMISSIONS.ledger.trialBalanceView,
  auditView: PERMISSIONS.ops.auditView,
  loansView: PERMISSIONS.loans.view,
  allBranches: PERMISSIONS.admin.allBranches,
} as const satisfies Record<string, PermissionCode>

/**
 * Any one of these is enough to have business in the ledger. Used to decide whether the module's
 * landing page is worth showing at all, rather than to authorise anything on it.
 */
export const LEDGER_MODULE_PERMISSIONS: readonly PermissionCode[] = [
  LEDGER_PERMISSIONS.accountView,
  LEDGER_PERMISSIONS.dayOpen,
  LEDGER_PERMISSIONS.dayClose,
  LEDGER_PERMISSIONS.driftView,
  LEDGER_PERMISSIONS.journalView,
  LEDGER_PERMISSIONS.periodClose,
  LEDGER_PERMISSIONS.provisionRun,
  LEDGER_PERMISSIONS.trialBalanceView,
]

export interface LedgerPermissions {
  /** Whether this user has any business in the ledger at all. */
  canEnterModule: boolean
  canOpenDay: boolean
  canCloseDay: boolean
  canViewJournals: boolean
  canPostManualJournal: boolean
  /** Holds the countersigning code for a manual journal — so their own may complete on the click. */
  canCountersignManualJournal: boolean
  canReverseJournal: boolean
  canCountersignReversal: boolean
  canViewAccounts: boolean
  canManageAccounts: boolean
  /** Holds `ledger.account.manage.approve` — so their own chart change may complete on the click. */
  canCountersignAccountManage: boolean
  canViewTrialBalance: boolean
  /** Reading the drift register. Disposing of an alarm is its own code — see below. */
  canViewDrift: boolean
  /**
   * Disposing of a drift alarm — `ledger.drift.resolve`, separate from viewing one because they
   * are different acts: accepting a difference with an explanation closes a control that would
   * otherwise block the day close.
   */
  canResolveDrift: boolean
  canClosePeriod: boolean
  canReopenPeriod: boolean
  canCountersignReopen: boolean
  /** Reopening the branch's most recent closed day — the maker's half of `ledger.day.reopen`. */
  canReopenDay: boolean
  canCountersignDayReopen: boolean
  canRunProvision: boolean
  /** Reading the audit trail — who acted, and who countersigned. Not a ledger permission. */
  canReadAudit: boolean
  /** Reading the classification scheme behind a provision run. Belongs to the loans module. */
  canReadSchemes: boolean
  /**
   * Held by head office. It widens what the server will answer for, but it does not enumerate:
   * `/access/me/branches` still lists only the home branch plus explicit grants, because no module
   * owns branches as records to enumerate from.
   */
  hasAllBranches: boolean
}

export function useLedgerPermissions(): LedgerPermissions {
  const { hasPermission, hasAnyPermission } = useAuth()

  return useMemo(
    () => ({
      canEnterModule: hasAnyPermission(LEDGER_MODULE_PERMISSIONS),
      canOpenDay: hasPermission(LEDGER_PERMISSIONS.dayOpen),
      canCloseDay: hasPermission(LEDGER_PERMISSIONS.dayClose),
      canViewJournals: hasPermission(LEDGER_PERMISSIONS.journalView),
      canPostManualJournal: hasPermission(LEDGER_PERMISSIONS.journalManual),
      canCountersignManualJournal: hasPermission(LEDGER_PERMISSIONS.journalManualApprove),
      canReverseJournal: hasPermission(LEDGER_PERMISSIONS.journalReverse),
      canCountersignReversal: hasPermission(LEDGER_PERMISSIONS.journalReverseApprove),
      canViewAccounts: hasPermission(LEDGER_PERMISSIONS.accountView),
      canManageAccounts: hasPermission(LEDGER_PERMISSIONS.accountManage),
      canCountersignAccountManage: hasPermission(LEDGER_PERMISSIONS.accountManageApprove),
      canViewTrialBalance: hasPermission(LEDGER_PERMISSIONS.trialBalanceView),
      canViewDrift: hasPermission(LEDGER_PERMISSIONS.driftView),
      canResolveDrift: hasPermission(LEDGER_PERMISSIONS.driftResolve),
      canClosePeriod: hasPermission(LEDGER_PERMISSIONS.periodClose),
      canReopenPeriod: hasPermission(LEDGER_PERMISSIONS.periodReopen),
      canCountersignReopen: hasPermission(LEDGER_PERMISSIONS.periodReopenApprove),
      canReopenDay: hasPermission(LEDGER_PERMISSIONS.dayReopen),
      canCountersignDayReopen: hasPermission(LEDGER_PERMISSIONS.dayReopenApprove),
      canRunProvision: hasPermission(LEDGER_PERMISSIONS.provisionRun),
      canReadAudit: hasPermission(LEDGER_PERMISSIONS.auditView),
      canReadSchemes: hasPermission(LEDGER_PERMISSIONS.loansView),
      hasAllBranches: hasPermission(LEDGER_PERMISSIONS.allBranches),
    }),
    [hasPermission, hasAnyPermission],
  )
}
